import { lazy, Suspense, Component, ReactNode } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { isLoggedIn } from './lib/auth'
import Layout from './components/Layout'
import Landing from './pages/Landing'
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import Timeline from './pages/Timeline'
import Alerts from './pages/Alerts'
import FamilyGraph from './pages/FamilyGraph'
import Consent from './pages/Consent'
import Ingest from './pages/Ingest'
import DoctorBrief from './pages/DoctorBrief'
import DevReport from './pages/DevReport'
import Engine from './pages/Engine'

// Lazy-load the d3-heavy page so a load failure is isolated
const KnowledgeGraph = lazy(() => import('./pages/KnowledgeGraph'))

// ─── Error boundary ───────────────────────────────────────────────────────────

interface EBState { error: Error | null }

class ErrorBoundary extends Component<{ children: ReactNode }, EBState> {
  state: EBState = { error: null }
  static getDerivedStateFromError(error: Error): EBState { return { error } }
  render() {
    if (this.state.error) {
      return (
        <div className="p-8 text-center">
          <p className="text-red-600 font-semibold mb-2">Something went wrong</p>
          <p className="text-sm text-gray-500">{this.state.error.message}</p>
          <button
            onClick={() => this.setState({ error: null })}
            className="mt-4 px-4 py-2 bg-brand-600 text-white rounded-lg text-sm"
          >
            Retry
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

function RequireAuth({ children }: { children: JSX.Element }) {
  return isLoggedIn() ? children : <Navigate to="/login" replace />
}

export default function App() {
  return (
    <ErrorBoundary>
      <Routes>
        <Route path="/" element={isLoggedIn() ? <Navigate to="/app" replace /> : <Landing />} />
        <Route path="/login" element={isLoggedIn() ? <Navigate to="/app" replace /> : <Login />} />
        <Route path="/report" element={<DevReport />} />
        <Route
          path="/app"
          element={
            <RequireAuth>
              <Layout />
            </RequireAuth>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="timeline" element={<Timeline />} />
          <Route path="alerts" element={<Alerts />} />
          <Route path="family" element={<FamilyGraph />} />
          <Route path="consent" element={<Consent />} />
          <Route path="ingest" element={<Ingest />} />
          <Route path="brief" element={<DoctorBrief />} />
          <Route path="report" element={<DevReport />} />
          <Route path="engine" element={<Engine />} />
          <Route
            path="graph"
            element={
              <ErrorBoundary>
                <Suspense fallback={<div className="p-8 text-sm text-gray-400">Loading graph…</div>}>
                  <KnowledgeGraph />
                </Suspense>
              </ErrorBoundary>
            }
          />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ErrorBoundary>
  )
}
